// src/utils/components/GoogleAutocomplete.jsx

import React, { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import useGoogleMaps from "~/provider/useGoogleMaps";
import { Autocomplete, TextField, Grid, Typography, Box } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";

const GoogleAutocomplete = forwardRef(({ getPlaceOption, locationValue }, ref) => {
  const { isLoaded } = useGoogleMaps();
  const [value, setValue] = useState(locationValue || null);
  const [inputValue, setInputValue] = useState(locationValue || "");
  const [options, setOptions] = useState([]);

  useImperativeHandle(ref, () => ({
    clearInput() {
      setValue(null);
      setInputValue("");
      setOptions([]);
    }
  }));

  useEffect(() => {
    setValue(locationValue);
    setInputValue(locationValue);
  }, [locationValue]);

  const fetchPredictions = useMemo(
    () =>
      throttle((input, callback) => {
        if (!window.google?.maps?.places) return;
        const service = new window.google.maps.places.AutocompleteService();
        service.getPlacePredictions({ input }, callback);
      }, 300),
    []
  );

  useEffect(() => {
    let active = true;
    if (!inputValue || !isLoaded) {
      setOptions([]);
      return;
    }

    fetchPredictions(inputValue, (results = []) => {
      if (active) setOptions(results);
    });

    return () => {
      active = false;
    };
  }, [inputValue, isLoaded, fetchPredictions]);

  if (!isLoaded) {
    return <TextField size="small" label="Loading..." fullWidth disabled />;
  }

  return (
    <Autocomplete
      filterOptions={(x) => x}
      options={options}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description || ""
      }
      value={value}
      onChange={(event, newValue) => {
        setValue(newValue);
        setInputValue(newValue ? newValue.description : "");

        if (!newValue || !newValue.place_id) return;

        const service = new window.google.maps.places.PlacesService(
          document.createElement("div")
        );

        service.getDetails(
          { placeId: newValue.place_id },
          (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              const geometry = place.geometry.location.toJSON();
              getPlaceOption({ ...newValue, geometry });
            }
          }
        );
      }}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      renderInput={(params) => (
        <TextField {...params} label="Add Location" size="small" fullWidth />
      )}
      renderOption={(props, option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings || [];

        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((m) => [m.offset, m.offset + m.length])
        );

        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item>
                <Box component={LocationOnIcon} sx={{ mr: 2 }} />
              </Grid>
              <Grid item xs>
                {parts.map((part, index) => (
                  <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                    {part.text}
                  </span>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
});

GoogleAutocomplete.propTypes = {
  getPlaceOption: PropTypes.func.isRequired,
  locationValue: PropTypes.string
};

export default GoogleAutocomplete;
